import { Card, Grid } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import {
    Box,
    Modal,
    Button,
    DataTable,
    FormField,
    EmailInput,
    Typography,
    Autocomplete,
    InputHtmlEditor,
    SelectMultiLanguage,
    IPropDetailContainer,
    useCommonComponentContext,
    RoleHelpers,
} from "@maysoft/common-component-react";

import Helpers from "../../commons/helpers";
import useStrings from "../../hooks/useStrings";
import MessageTemplateService from "../../services/message/MessageTemplate.service";
import TemplateCaseKeywordService from "../../services/message/TemplateCaseKeyword.service";

import { Constants } from "../../constants/constants";
import { MessageTemplateType, Mode } from "../../constants/enums";
import { IMultiLanguageContent, IRecordTemplateKeyword } from "../../commons/interfaces";
import useServiceCodenames from "../../hooks/messageTemplate/useServiceCodenames";
import useTenantCodenames from "../../hooks/messageTemplate/useTenantCodenames";


interface IDataDetail {
    id: string;
    type: string;
    templateName: string;
    title: IMultiLanguageContent;
    content: IMultiLanguageContent;
    from: string;
    to: string[];
    cc: string[];
    bcc: string[];
    tenantCode: string;
    serviceCode: string;
    organizationId: string;
}

type IError = { [k in keyof IDataDetail]?: string };

const REQUIRED_MULTI_LANG_KEY = ["title", "content"];

export interface IMessageTemplateEditContainerProps extends IPropDetailContainer {
    detailInitialData?: {
        tenantCode?: string;
        serviceCode?: string;
    }
}

const MessageTemplateEditContainer = (props: IMessageTemplateEditContainerProps) => {
    const Strings = useStrings();

    const { language, userInfo, tenantCode: commonTenantCode, serviceCode: commonServiceCode, listMultiLanguage, onError, onSuccess, onHideLoading, onShowLoading } =
        useCommonComponentContext();
    const organizationId = userInfo?.userProfile?.organizationId;
    const isServiceAdmin = RoleHelpers.isServiceAdmin(userInfo?.userProfile?.roleType);
    const serviceCode = isServiceAdmin && props.detailInitialData?.serviceCode ? props.detailInitialData?.serviceCode : commonServiceCode;
    const tenantCode = isServiceAdmin && props.detailInitialData?.tenantCode ? props.detailInitialData?.tenantCode : commonTenantCode;

    const [errorDetail, setErrorDetail] = useState<IError>({} as IError);
    const [dataDetail, setDataDetail] = useState<IDataDetail>({} as IDataDetail);

    const [templateKeyWords, setTemplateKeyWords] = useState<IRecordTemplateKeyword[]>([]);
    const [dataModal, setDataModal] = useState<{
        open: boolean;
        data?: IRecordTemplateKeyword;
        error?: { keyword?: string; value?: string };
    }>({ open: false });

    const [isEdited, setIsEdited] = useState<boolean>(false);
    const [valLanguage, setValLanguage] = useState<string>(Constants.DefaultLanguage);

    const MessageTemplateTypeData = [
        {
            code: `${MessageTemplateType.Email}`,
            name: Strings.Common.EMAIL,
        },
        {
            code: `${MessageTemplateType.SMS}`,
            name: Strings.Common.SMS,
        },
        {
            code: `${MessageTemplateType.Notification}`,
            name: Strings.Common.NOTIFICATION,
        },
    ];

    const mode = useMemo(() => props.mode, [props.mode]);

    useEffect(() => setValLanguage(language), [language]);
    const { serviceCodenames } = useServiceCodenames({ organizationId });
    const { tenantCodenames } = useTenantCodenames({ organizationId });

    useEffect(() => {
        getDetail(props.idDetail);
    }, [props.idDetail, organizationId]);

    const getDetail = async (id?: string) => {
        if (Helpers.isNullOrEmpty(id)) {
            setDataDetail({
                id: undefined,
                templateName: "",
                type: `${MessageTemplateType.Email}`,
                title: {
                    value: { [valLanguage]: "" },
                },
                content: {
                    value: { [valLanguage]: "" },
                },
                from: null,
                to: [] as string[],
                cc: [] as string[],
                bcc: [] as string[],

                tenantCode: tenantCode,
                serviceCode: serviceCode,
                organizationId: organizationId || "0",
            });
        } else {
            try {
                onShowLoading();

                const result = await MessageTemplateService.getDetail(id);

                setDataDetail({
                    id: result?.id,
                    title: result?.title,
                    type: `${result?.type}`,
                    templateName: result?.templateName,
                    content: result?.content,
                    from: result?.from,
                    cc: !Helpers.isNullOrEmpty(result?.cc) ? result?.cc?.split(";") : [],
                    to: !Helpers.isNullOrEmpty(result?.to) ? result?.to?.split(";") : [],
                    bcc: !Helpers.isNullOrEmpty(result?.bcc) ? result?.bcc?.split(";") : [],

                    tenantCode: result?.tenantCode,
                    serviceCode: result?.serviceCode,
                    organizationId: result?.organizationId,
                });

                setTemplateKeyWords([...(result?.templateKeyWords || [])]);

                onHideLoading();
            } catch (error) {
                onHideLoading();
                const e = Helpers.renderExceptionError(error);
                onError(e);
            }
        }
    };

    const onChangeValue = async (value: any, key: string) => {
        if (REQUIRED_MULTI_LANG_KEY.includes(key)) {
            let temp: any = { ...dataDetail };

            temp[key] = {
                ...temp[key],
                value: {
                    ...temp[key]?.value,
                    [valLanguage]: value,
                },
            };

            setDataDetail(temp);
        } else {
            const temp = { ...dataDetail };

            setDataDetail((prev) => ({
                ...prev,
                ...temp,
                [key]: key === "from" ? value[0] : value,
            }));
        }

        setIsEdited(true);
        setErrorDetail((prev) => ({ ...prev, [key]: "" }));
    };

    const handleCheckValidate = () => {
        let isChecked: boolean = true;
        let dataError: any = { ...errorDetail };

        if (isServiceAdmin && Helpers.isNullOrEmpty(dataDetail?.tenantCode)) {
            isChecked = false;
            dataError["tenantCode"] = Strings.Common.REQUIRED;
        }
        if (isServiceAdmin && Helpers.isNullOrEmpty(dataDetail?.serviceCode)) {
            isChecked = false;
            dataError["serviceCode"] = Strings.Common.REQUIRED;
        }

        if (Helpers.isNullOrEmpty(dataDetail?.type)) {
            isChecked = false;
            dataError["type"] = Strings.Common.REQUIRED;
        }
        if (Helpers.isNullOrEmpty(dataDetail?.templateName)) {
            isChecked = false;
            dataError["templateName"] = Strings.Common.REQUIRED;
        }
        if (Helpers.isNullOrEmpty(dataDetail?.title?.value?.[valLanguage])) {
            isChecked = false;
            dataError["title"] = Strings.Common.REQUIRED;
        }
        if (Helpers.isNullOrEmpty(dataDetail?.content?.value?.[valLanguage])) {
            isChecked = false;
            dataError["content"] = Strings.Common.REQUIRED;
        }

        // if (Number(dataDetail?.type) === MessageTemplateType.Email) {
        //     if (Helpers.isNullOrEmpty(dataDetail?.from)) {
        //         isChecked = false;
        //         dataError["from"] = Strings.Common.REQUIRED;
        //     };
        //     if (Helpers.isNullOrEmpty(dataDetail?.to)) {
        //         isChecked = false;
        //         dataError["to"] = Strings.Common.REQUIRED;
        //     };
        //     if (Helpers.isNullOrEmpty(dataDetail?.bcc)) {
        //         isChecked = false;
        //         dataError["bcc"] = Strings.Common.REQUIRED;
        //     };
        //     if (Helpers.isNullOrEmpty(dataDetail?.cc)) {
        //         isChecked = false;
        //         dataError["cc"] = Strings.Common.REQUIRED;
        //     };
        // }

        if (!isChecked) {
            setErrorDetail(dataError);
        }

        return isChecked;
    };

    const handleSubmit = async () => {
        if (mode === Mode.View) {
            props.onChangeMode(Mode.Update);
            return;
        } else {
            if (!handleCheckValidate()) {
                return;
            } else {
                try {
                    onShowLoading();

                    const newType = Helpers.isNullOrEmpty(dataDetail?.type) ? MessageTemplateType.Email : Number(dataDetail?.type);

                    const newTitle = Helpers.getDataMultiLanguage({
                        data: dataDetail.title,
                        currentLanguage: language,
                        listLanguage: listMultiLanguage,
                    });

                    const newContent = Helpers.getDataMultiLanguage({
                        data: dataDetail.content,
                        currentLanguage: language,
                        listLanguage: listMultiLanguage,
                    });

                    if (Helpers.isNullOrEmpty(dataDetail?.id)) {
                        await MessageTemplateService.create({
                            ...dataDetail,
                            type: newType,
                            title: newTitle,
                            content: newContent,
                            templateKeyWords: templateKeyWords,
                        });

                        onSuccess(Strings.MESSAGE_TEMPLATE.CREATE_SUCCESS);

                        props.onGoBack();
                    } else {
                        const result = await MessageTemplateService.update({
                            ...dataDetail,
                            type: newType,
                            title: newTitle,
                            content: newContent,
                        });

                        setIsEdited(false);

                        await getDetail(result?.id);

                        props.onChangeMode(Mode.View);

                        onSuccess(Strings.MESSAGE_TEMPLATE.UPDATE_SUCCESS);
                    }
                } catch (error) {
                    onHideLoading();
                    const e = Helpers.renderExceptionError(error);
                    onError(e);
                } finally {
                    onHideLoading();
                }
            }
        }
    };

    // Template Keyword //

    const onDeleteRecordTemplateKeyword = async (item: IRecordTemplateKeyword) => {
        try {
            onShowLoading();

            let temp = [...(templateKeyWords || [])];

            temp = temp.filter((el) => el.id !== item.id);

            if (!Helpers.isNullOrEmpty(dataDetail?.id) && !Helpers.isNullOrEmpty(item?.id)) {
                await TemplateCaseKeywordService.delete(item.id);
            }

            setTemplateKeyWords(temp);
            setIsEdited(true);
        } catch (error) {
            onHideLoading();
            const e = Helpers.renderExceptionError(error);
            onError(e);
        } finally {
            onHideLoading();
        }
    };

    const onCreateOrUpdateRecordKeyword = async (item: IRecordTemplateKeyword) => {
        try {
            onShowLoading();

            let result: any;
            const temp = [...(templateKeyWords || [])];
            const valueMulti = Helpers.getDataMultiLanguage({
                data: item.value,
                currentLanguage: language,
                listLanguage: listMultiLanguage,
            });

            if (!Helpers.isNullOrEmpty(item?.id)) {
                result = await TemplateCaseKeywordService.update({
                    id: item?.id,
                    value: valueMulti,
                    keyword: item?.keyword,
                    targetId: item?.targetId,
                });

                const index = temp.findIndex((el) => el.id === item.id);
                temp[index] = result;
            } else {
                result = await TemplateCaseKeywordService.create({
                    targetId: dataDetail?.id,
                    keyword: item?.keyword,
                    value: valueMulti,
                });
                temp.push(result);
            }

            setTemplateKeyWords(temp);
            setIsEdited(true);
        } catch (error) {
            onHideLoading();
            const e = Helpers.renderExceptionError(error);
            onError(e);
        } finally {
            onHideLoading();
        }
    };

    const handleSubmitModal = () => {
        let idChecked: boolean = true;
        let dataTemp: any = { ...dataModal?.data };
        let dataError: any = { ...dataModal?.error };

        if (Helpers.isNullOrEmpty(dataTemp?.["value"]?.value?.[valLanguage])) {
            idChecked = false;
            dataError["value"] = Strings.Common.REQUIRED;
        }

        if (Helpers.isNullOrEmpty(dataTemp?.["keyword"])) {
            idChecked = false;
            dataError["keyword"] = Strings.Common.REQUIRED;
        }

        if (!idChecked) {
            setDataModal({ open: true, data: dataTemp, error: dataError });
        } else {
            setDataModal({ open: false });
            if (Helpers.isNullOrEmpty(dataDetail?.id)) {
                // mode create
                const newListTemp = [...(templateKeyWords || [])];
                const valueMulti = Helpers.getDataMultiLanguage({
                    data: dataTemp.value,
                    currentLanguage: language,
                    listLanguage: listMultiLanguage,
                });

                if (Helpers.isNullOrEmpty(dataTemp?.id)) {
                    newListTemp.push({
                        ...dataTemp,
                        value: valueMulti,
                        id: Date.now().toString(),
                    });
                } else {
                    const index = newListTemp.findIndex((el) => el.id === dataTemp.id);
                    newListTemp[index] = {
                        ...dataTemp,
                        value: valueMulti,
                    };
                }

                setTemplateKeyWords(newListTemp);
                setIsEdited(true);
            } else {
                // mode update
                onCreateOrUpdateRecordKeyword(dataTemp);
            }
        }
    };

    const renderModal = () => (
        <Modal
            fullWidth
            maxWidth="sm"
            hasActionButton
            visible={dataModal?.open || false}
            title={dataModal?.data?.id ? "Cap nhat tu khoa" : "Them tu khoa moi"}
            onClose={() => {
                setDataModal({ open: false });
            }}
            onAction={() => {
                handleSubmitModal();
            }}
        >
            <Grid container spacing={3} p={2}>
                <Grid item xs={12}>
                    <FormField
                        required
                        mode={mode}
                        maxLength={255}
                        label={Strings.MESSAGE_TEMPLATE.KEYWORD}
                        errorMessage={dataModal?.error?.keyword}
                        defaultValue={dataModal?.data?.keyword || ""}
                        placeholder={Strings.MESSAGE_TEMPLATE.INPUT_KEYWORD}
                        onBlur={(value) => {
                            setDataModal({
                                open: true,
                                data: {
                                    ...dataModal?.data,
                                    keyword: value,
                                },
                                error: {
                                    ...dataModal?.error,
                                    keyword: undefined,
                                },
                            });
                        }}
                    />
                </Grid>
                <Grid item xs={12}>
                    <FormField
                        required
                        mode={mode}
                        maxLength={255}
                        label={Strings.MESSAGE_TEMPLATE.VALUE}
                        errorMessage={dataModal?.error?.value}
                        placeholder={Strings.MESSAGE_TEMPLATE.INPUT_VALUE}
                        value={dataModal?.data?.value?.value?.[valLanguage]}
                        onChangeValue={(value) => {
                            const newValue = {
                                ...dataModal?.data?.value,
                                value: {
                                    ...dataModal?.data?.value?.value,
                                    [valLanguage]: value,
                                },
                            };

                            setDataModal({
                                open: true,
                                data: {
                                    ...dataModal?.data,
                                    value: newValue,
                                },
                                error: {
                                    ...dataModal?.error,
                                    value: undefined,
                                },
                            });
                        }}
                    />
                </Grid>
            </Grid>
        </Modal>
    );

    return (
        <Box>
            <Box sx={{ display: "flex", alignItems: "flex-end", justifyContent: "end", mb: 3 }}>
                <Button
                    color={"secondary"}
                    onClick={() => {
                        if (isEdited) {
                            Helpers.showConfirmAlert(Strings.Common.CANCEL_ACTION_MESSAGE, props.onGoBack);
                        } else {
                            props.onGoBack();
                        }
                    }}
                >
                    {Strings.Common.GO_BACK}
                </Button>
                <Button
                    sx={{ ml: 2 }}
                    color={"dark"}
                    onClick={() => {
                        handleSubmit();
                    }}
                >
                    {mode === Mode.View ? Strings.Common.EDIT : Strings.Common.SAVE}
                </Button>
            </Box>

            {renderModal()}

            <Card>
                <Box p={2}>
                    <Box sx={{ mb: 1 }} display="flex" flexWrap="wrap" alignItems="center" justifyContent="space-between">
                        <Typography variant="h5">{Strings.MESSAGE_TEMPLATE.BASIC_INFO}</Typography>
                        <SelectMultiLanguage
                            defaultLanguage={valLanguage}
                            onChangeLanguage={(value) => {
                                const newData = {
                                    ...dataDetail,
                                    title: Helpers.getDataMultiLanguage({
                                        data: dataDetail.title,
                                        currentLanguage: language,
                                        listLanguage: listMultiLanguage,
                                    }),
                                    content: Helpers.getDataMultiLanguage({
                                        data: dataDetail.content,
                                        currentLanguage: language,
                                        listLanguage: listMultiLanguage,
                                    }),
                                };

                                setValLanguage(value);
                                setDataDetail(newData);
                            }}
                        />
                    </Box>
                    <Grid container spacing={3} p={2}>
                        {isServiceAdmin && <Grid item xs={12} md={6}>
                            <Autocomplete
                                required
                                mode={mode}
                                data={tenantCodenames || []}
                                defaultValue={dataDetail?.tenantCode}
                                label={Strings.MESSAGE_TEMPLATE.TENANT}
                                errorMessage={errorDetail?.tenantCode || ""}
                                placeholder={Strings.MESSAGE_TEMPLATE.SELECT_TENANT}
                                onChange={(value) => onChangeValue(value, "tenantCode")}
                            />
                        </Grid>}
                        {isServiceAdmin && <Grid item xs={12} md={6}>
                            <Autocomplete
                                required
                                mode={mode}
                                data={serviceCodenames || []}
                                defaultValue={dataDetail?.serviceCode}
                                label={Strings.MESSAGE_TEMPLATE.SERVICE}
                                errorMessage={errorDetail?.tenantCode || ""}
                                placeholder={Strings.MESSAGE_TEMPLATE.SELECT_SERVICE}
                                onChange={(value) => onChangeValue(value, "serviceCode")}
                            />
                        </Grid>}
                        <Grid item xs={12} md={6}>
                            <Autocomplete
                                required
                                mode={mode}
                                defaultValue={dataDetail?.type}
                                data={MessageTemplateTypeData || []}
                                label={Strings.MESSAGE_TEMPLATE.TYPE}
                                errorMessage={errorDetail?.type || ""}
                                disabled={!Helpers.isNullOrEmpty(dataDetail?.id)}
                                placeholder={Strings.MESSAGE_TEMPLATE.SELECT_TYPE}
                                onChange={(value) => onChangeValue(value, "type")}
                            />
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <FormField
                                required
                                mode={mode}
                                maxLength={255}
                                label={Strings.MESSAGE_TEMPLATE.NAME}
                                defaultValue={dataDetail?.templateName || ""}
                                errorMessage={errorDetail?.templateName || ""}
                                placeholder={Strings.MESSAGE_TEMPLATE.INPUT_NAME}
                                onBlur={(value) => onChangeValue(value, "templateName")}
                            />
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <FormField
                                required
                                mode={mode}
                                maxLength={255}
                                label={Strings.MESSAGE_TEMPLATE.TITLE}
                                errorMessage={errorDetail?.title || ""}
                                placeholder={Strings.MESSAGE_TEMPLATE.INPUT_TITLE}
                                defaultValue={dataDetail?.title?.value?.[valLanguage]}
                                onBlur={(value) => onChangeValue(value, "title")}
                            />
                        </Grid>

                        {Number(dataDetail?.type) === MessageTemplateType.Email && (
                            <>
                                <Grid item xs={12} md={6}>
                                    <EmailInput
                                        mode={mode}
                                        maxEmails={1}
                                        label={Strings.MESSAGE_TEMPLATE.FROM}
                                        errorMessage={errorDetail?.from || ""}
                                        placeholder={Strings.MESSAGE_TEMPLATE.INPUT_FROM}
                                        value={dataDetail?.from ? [dataDetail?.from] : [] || []}
                                        onChangeValue={(value) => onChangeValue(value, "from")}
                                    />
                                </Grid>

                                <Grid item xs={12} md={6}>
                                    <EmailInput
                                        mode={mode}
                                        value={dataDetail?.to || []}
                                        label={Strings.MESSAGE_TEMPLATE.TO}
                                        errorMessage={errorDetail?.to || ""}
                                        placeholder={Strings.MESSAGE_TEMPLATE.INPUT_TO}
                                        onChangeValue={(value) => onChangeValue(value, "to")}
                                    />
                                </Grid>

                                <Grid item xs={12} md={6}>
                                    <EmailInput
                                        mode={mode}
                                        value={dataDetail?.cc || []}
                                        label={Strings.MESSAGE_TEMPLATE.CC}
                                        errorMessage={errorDetail?.cc || ""}
                                        placeholder={Strings.MESSAGE_TEMPLATE.INPUT_CC}
                                        onChangeValue={(value) => onChangeValue(value, "cc")}
                                    />
                                </Grid>

                                <Grid item xs={12} md={6}>
                                    <EmailInput
                                        mode={mode}
                                        value={dataDetail?.bcc || []}
                                        label={Strings.MESSAGE_TEMPLATE.BCC}
                                        errorMessage={errorDetail?.bcc || ""}
                                        placeholder={Strings.MESSAGE_TEMPLATE.INPUT_BCC}
                                        onChangeValue={(value) => onChangeValue(value, "bcc")}
                                    />
                                </Grid>
                            </>
                        )}

                        <Grid item xs={12}>
                            <InputHtmlEditor
                                key={errorDetail?.content || ""}
                                readonly={mode === Mode.View}
                                errorMessage={errorDetail?.content}
                                onBlur={(value) => onChangeValue(value, "content")}
                                value={dataDetail?.content?.value?.[valLanguage] || ""}
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <Box mb={1} display="flex" alignItems="center" justifyContent="space-between">
                                <Typography variant="h6">{Strings.MESSAGE_TEMPLATE.KEYWORD_LIST}</Typography>
                                {mode !== Mode.View && (
                                    <Button
                                        variant="outlined"
                                        color="success"
                                        onClick={() => {
                                            setIsEdited(true);
                                            setDataModal({
                                                open: true,
                                                data: {
                                                    keyword: "",
                                                    value: {
                                                        value: { [valLanguage]: "" },
                                                    },
                                                },
                                            });
                                        }}
                                    >
                                        {Strings.Common.ADD_NEW}
                                    </Button>
                                )}
                            </Box>

                            <DataTable
                                isLocal
                                mode={mode}
                                isShowIndex
                                pageNumber={1}
                                rowPerPage={Constants.ROW_PER_PAGE}
                                totalCount={[...(templateKeyWords || [])].length}
                                table={{
                                    columns: [
                                        {
                                            Header: Strings.MESSAGE_TEMPLATE.KEYWORD,
                                            accessor: "keyword",
                                        },
                                        {
                                            Header: Strings.MESSAGE_TEMPLATE.VALUE,
                                            accessor: "value",
                                            Cell: ({ value }: any) => (
                                                <>{Helpers.renderValueByLanguage({ data: value, currentLanguage: language })}</>
                                            ),
                                        },
                                    ],
                                    rows: [...(templateKeyWords || [])],
                                }}
                                actionList={(row) => [
                                    {
                                        key: "edit",
                                        iconName: "edit",
                                        title: Strings.Common.EDIT,
                                        callback: (row) =>
                                            setDataModal({
                                                open: true,
                                                data: row,
                                            }),
                                    },
                                    {
                                        key: "delete",
                                        iconName: "delete",
                                        title: Strings.Common.DELETE,
                                        callback: (row) => onDeleteRecordTemplateKeyword(row),
                                    },
                                ]}
                            />
                        </Grid>
                    </Grid>
                </Box>
            </Card>
        </Box>
    );
};

export default MessageTemplateEditContainer;
